export type DiffKind = "added" | "removed" | "same";

export interface DiffSegment {
	kind: DiffKind;
	text: string;
}

/**
 * Word-level diff of two short texts, for reading a scene-facts change at a
 * glance: what the camera has now against what would replace it.
 *
 * A plain longest-common-subsequence over words and the whitespace between
 * them. Scene facts are capped at a few hundred characters, so the quadratic
 * table stays small and no diff library is worth adding for it. Where a word
 * was replaced, the removal comes before the addition, the way people read a
 * correction.
 */
export const diffWords = (before: string, after: string): DiffSegment[] => {
	const a = tokenize(before);
	const b = tokenize(after);

	// Suffix LCS lengths, flattened: table[i * width + j] is the length of the
	// longest common subsequence of a[i..] and b[j..].
	const width = b.length + 1;
	const table = new Array<number>((a.length + 1) * width).fill(0);
	const at = (i: number, j: number) => table[i * width + j] ?? 0;
	for (let i = a.length - 1; i >= 0; i--) {
		for (let j = b.length - 1; j >= 0; j--) {
			table[i * width + j] =
				a[i] === b[j]
					? at(i + 1, j + 1) + 1
					: Math.max(at(i + 1, j), at(i, j + 1));
		}
	}

	const segments: DiffSegment[] = [];
	const push = (kind: DiffKind, text: string | undefined) => {
		if (!text) return;
		const last = segments[segments.length - 1];
		if (last?.kind === kind) {
			last.text += text;
		} else {
			segments.push({ kind, text });
		}
	};

	let i = 0;
	let j = 0;
	while (i < a.length && j < b.length) {
		if (a[i] === b[j]) {
			push("same", a[i]);
			i++;
			j++;
		} else if (at(i + 1, j) >= at(i, j + 1)) {
			push("removed", a[i]);
			i++;
		} else {
			push("added", b[j]);
			j++;
		}
	}
	for (; i < a.length; i++) push("removed", a[i]);
	for (; j < b.length; j++) push("added", b[j]);

	return segments;
};

/** True when the diff holds anything other than unchanged text. */
export const hasChanges = (segments: DiffSegment[]): boolean =>
	segments.some((segment) => segment.kind !== "same");

/** Words and the runs of whitespace between them, both kept as tokens. */
const tokenize = (text: string): string[] =>
	text.split(/(\s+)/).filter((token) => token.length > 0);
